import { useContext } from 'react';

import classNames from 'classnames';
import { useAtomValue } from 'jotai';
import { useHistory } from 'react-router-dom';

import { ExternalPatientContext } from '@/app/external/constants';

import type { Appointment } from '../../../../../react-query/useAppointments';
import type { TVisitSummary } from '../../../../../react-query/useVisitSummaries';
import {
  selectQuadrantsAtom,
  selectTeethAtom,
  selectedCountsFilterAtom,
} from '../../../atoms';
import { useReviewRadiographs } from '../../useReviewRadiographs';
import type { ToothStatus } from '../Tooth';
import { Tooth } from '../Tooth';
import { ToothHover } from '../toothHover';
import type { TDisplayOrderQuadrantAbbreviations } from '../types';
import { useToothSelection } from '../useToothSelection';
import {
  getIsVisualizationFilteredOut,
  isLowerQuadrant,
  isUpperQuadrant,
} from '../utils';

type QuadrantToothProps = {
  activeImageId?: string;
  toothStatus: ToothStatus;
  currentToothIndex: number;
  lastToothIndex: number;
  quadrant: TDisplayOrderQuadrantAbbreviations;
  visitSummary?: TVisitSummary;
  appointment?: Appointment;
  module?: 'image-viewer' | 'patient-list';
  scaleFactor?: number;
  isSpotlight?: boolean;
};

export const QuadrantTooth = ({
  activeImageId,
  toothStatus,
  currentToothIndex,
  lastToothIndex,
  quadrant,
  visitSummary,
  appointment,
  module,
  scaleFactor,
  isSpotlight,
}: QuadrantToothProps) => {
  const selectedTeeth = useAtomValue(selectTeethAtom);

  const selectedQuadrants = useAtomValue(selectQuadrantsAtom);
  const selectedCountsFilter = useAtomValue(selectedCountsFilterAtom);
  const { handleToothSelection } = useToothSelection(appointment);

  const { isIframe } = useContext(ExternalPatientContext);
  const history = useHistory();
  const { onReviewRadiographs, annotatorUri } =
    useReviewRadiographs(appointment);

  const isFirst = currentToothIndex === 0;
  const isLast = currentToothIndex === lastToothIndex;
  const visitSummaryTooth = visitSummary?.teeth.find(
    ({ toothId }) => toothId === toothStatus.toothId,
  );
  const isCursorNotAllowed =
    !isIframe &&
    (!visitSummaryTooth ||
      !visitSummaryTooth?.hasAnalysis ||
      (module === 'patient-list' &&
        !appointment?.appointment.estimatedVisitId));
  const isToothSelectionDisabled = isIframe || isCursorNotAllowed;

  const isFilteredOut = getIsVisualizationFilteredOut(
    toothStatus.type,
    toothStatus.status,
    selectedCountsFilter,
    module,
  );

  const outerElClassNames = classNames(
    `toothId-${toothStatus.toothId}`,
    'vai-max-w-full vai-object-contain vai-grid vai-flex-1 vai-box-border vai-z-10',
    {
      'vai-self-end vai-pb-[2px]': isUpperQuadrant(quadrant),
      'vai-self-start vai-pt-[2px]': isLowerQuadrant(quadrant),
      'vai-pl-[2px]': isFirst,
      'vai-pr-[2px]': isLast,
      'vai-cursor-not-allowed': isCursorNotAllowed,
      'vai-opacity-45': isFilteredOut,

      // Spotlight effect: The ::before pseudo-element is the tab, and the ::after pseudo-element is the gradient.
      // Pseudo-element height, width, and positioning
      'before:vai-h-1 before:vai-absolute after:vai-h-full after:vai-absolute after:-vai-z-10':
        isSpotlight,
      'before:vai-rounded-b before:vai-top-[1px] after:vai-top-[1px]':
        isUpperQuadrant(quadrant),
      'before:vai-rounded-t before:vai-bottom-[1px] after:vai-bottom-[1px]':
        isLowerQuadrant(quadrant),
      'before:vai-w-[calc(var(--tooth-width)-4px)] after:vai-w-[calc(var(--tooth-width)-4px)] before:vai-ml-[2px] after:vai-ml-[2px]':
        isSpotlight && !isFirst && !isLast,
      'before:vai-w-[calc(var(--tooth-width)-8px)] after:vai-w-[calc(var(--tooth-width)-8px)] before:vai-ml-[2px] after:vai-ml-[2px]':
        isSpotlight && (isFirst || isLast),
      // Pseudo-element colors
      'before:vai-bg-red-500': toothStatus.status === 'unscheduled',
      'before:vai-bg-purple-700':
        toothStatus.status === 'recommended' && module !== 'image-viewer',
      'before:vai-bg-violet-700':
        toothStatus.status === 'recommended' && module === 'image-viewer',
      // Light mode
      'after:vai-bg-[linear-gradient(to_bottom,rgba(255,226,223,.5)_0px,rgba(0,0,0,0)_100%)]':
        toothStatus.status === 'unscheduled' &&
        isUpperQuadrant(quadrant) &&
        module !== 'image-viewer',
      'after:vai-bg-[linear-gradient(to_bottom,rgba(243,232,255,1)_0px,rgba(0,0,0,0)_100%)]':
        toothStatus.status === 'recommended' &&
        isUpperQuadrant(quadrant) &&
        module !== 'image-viewer',
      'after:vai-bg-[linear-gradient(to_top,rgba(255,226,223,.5)_0px,rgba(0,0,0,0)_100%)]':
        toothStatus.status === 'unscheduled' &&
        isLowerQuadrant(quadrant) &&
        module !== 'image-viewer',
      'after:vai-bg-[linear-gradient(to_top,rgba(243,232,255,1)_0px,rgba(0,0,0,0)_100%)]':
        toothStatus.status === 'recommended' &&
        isLowerQuadrant(quadrant) &&
        module !== 'image-viewer',
      // Dark mode
      'after:vai-bg-[linear-gradient(to_bottom,rgba(128,19,19,.5)_0px,rgba(24,24,27,0)_100%)]':
        toothStatus.status === 'unscheduled' &&
        isUpperQuadrant(quadrant) &&
        module === 'image-viewer',
      'after:vai-bg-[linear-gradient(to_bottom,rgba(77,19,128,1)_0px,rgba(33,8,55,0)_100%)]':
        toothStatus.status === 'recommended' &&
        isUpperQuadrant(quadrant) &&
        module === 'image-viewer',
      'after:vai-bg-[linear-gradient(to_top,rgba(128,19,19,.5)_0px,rgba(24,24,27,0)_100%)]':
        toothStatus.status === 'unscheduled' &&
        isLowerQuadrant(quadrant) &&
        module === 'image-viewer',
      'after:vai-bg-[linear-gradient(to_top,rgba(77,19,128,1)_0px,rgba(33,8,55,0)_100%)]':
        toothStatus.status === 'recommended' &&
        isLowerQuadrant(quadrant) &&
        module === 'image-viewer',
    },
  );

  const innerElClassNames = classNames(
    'vai-px-[1px] vai-border vai-rounded-[2px]',
    {
      'vai-border-transparent':
        !selectedTeeth.includes(toothStatus.toothId) ||
        selectedQuadrants.length,
      'vai-bg-[#3F3F41] vai-border-[#778497]':
        selectedTeeth.includes(toothStatus.toothId) &&
        !selectedQuadrants.length &&
        module === 'image-viewer',
      'vai-pb-[1px]': isUpperQuadrant(quadrant),
      'vai-pt-[1px]': isLowerQuadrant(quadrant),
    },
  );

  const handleRef = (el: HTMLDivElement | HTMLButtonElement | null) => {
    if (el) {
      // Since our pseudo-elements are absolutely positioned relative to the
      // quadrant, setting this property gives them context into the width of
      // the individual tooth.
      el.style.setProperty('--tooth-width', `${el.offsetWidth}px`);
    }
  };

  const toothRender = isToothSelectionDisabled ? (
    <div
      className={innerElClassNames}
      data-testid={`tooth-${toothStatus.toothId}`}
    >
      <ToothHover
        activeImageId={activeImageId}
        appointment={appointment}
        module={module}
        visitSummaryTooth={visitSummaryTooth}
        toothStatus={toothStatus}
        visitSummary={visitSummary}
      >
        <Tooth
          isDarkMode={module === 'image-viewer'}
          wx={scaleFactor}
          hx={scaleFactor}
          {...toothStatus}
        />
      </ToothHover>
    </div>
  ) : (
    <button
      key={toothStatus.toothId}
      data-testid={`tooth-${toothStatus.toothId}`}
      className={innerElClassNames}
      onClick={(e) => {
        e.stopPropagation();
        handleToothSelection(toothStatus.toothId, module);

        if (module === 'patient-list') {
          onReviewRadiographs();
          history.push(annotatorUri);
        }
      }}
    >
      <ToothHover
        activeImageId={activeImageId}
        appointment={appointment}
        module={module}
        visitSummaryTooth={visitSummaryTooth}
        toothStatus={toothStatus}
        visitSummary={visitSummary}
      >
        <Tooth
          isDarkMode={module === 'image-viewer'}
          wx={scaleFactor}
          hx={scaleFactor}
          {...toothStatus}
        />
      </ToothHover>
    </button>
  );

  return (
    <div className={outerElClassNames} ref={handleRef}>
      {toothRender}
    </div>
  );
};
